import * as Yup from "yup";

import { Modal, OfficeButton } from "components";
import { PromotionAssets, PromotionType } from "_graphql/__generated__/globalTypes";
import {
  UpdatePromotion,
  UpdatePromotionVariables,
} from "_graphql/mutation/__generated__/UpdatePromotion";
import { useEffect, useRef } from "react";

import { PromotionForm } from "components/forms";
import toast from "react-hot-toast";
import { updatePromotion } from "_graphql/visal/mutations/promotions";
import { useAppSearch } from "_graphql/cache/auth";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { usePromotion } from "hooks/data/use-promotions";
import { wrapClick } from "utils";

const validationSchema = Yup.object().shape({
  input: Yup.object().shape({
    id: Yup.string().required("Promotion ID is required"),
    description: Yup.string().required("Description is required"),
    end_datetime: Yup.date().required("End date is required"),
    name: Yup.string().required("Name is required"),
    start_datetime: Yup.date().required("Start date is required"),
    type: Yup.string().required("Type is required"),
  }),
});

export default function UpdateContainer({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  const searchParams = useAppSearch();
  const { loading, promotion, refetch } = usePromotion({
    getPromotionId: searchParams?.id || "",
  });

  // Use a ref to track if we've initialized the form
  const initialized = useRef(false);

  const [execute, { loading: updating }] = useMutation<
    UpdatePromotion,
    UpdatePromotionVariables
  >(updatePromotion, {
    refetchQueries: ["GetPromotions"],
  });

  const form = useFormik<UpdatePromotionVariables>({
    initialValues: {
      input: {
        id: searchParams?.id || "",
        description: "",
        end_datetime: "",
        name: "",
        start_datetime: "",
        type: PromotionType.SHORT_TERM,
        assets: {
          small: [],
          medium: [],
          large: [],
        },
      },
    },
    enableReinitialize: false,
    validateOnChange: true,
    validationSchema,
    onSubmit: async (variables, helpers) => {
      execute({
        variables: variables,
      })
        .then(({ data }) => {
          if (data?.updatePromotion?.id) {
            toast.success("Promotion updated successfully");
            setOpen(false);
            helpers.resetForm();
            refetch();
          } else {
            toast.error("Failed to update promotion");
          }
        })
        .catch((err) => {
          toast.error(err.message);
        });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  // Only set values once when redemption data is available and not already initialized
  useEffect(() => {
    if (promotion) {
      form.setValues({
        input: {
          id: promotion?.id || "",
          description: promotion?.description || "",
          end_datetime: promotion?.end_datetime || "",
          name: promotion?.name || "",
          start_datetime: promotion?.start_datetime || "",
          type: promotion?.type as PromotionType || PromotionType.SHORT_TERM,
          assets: promotion?.assets as PromotionAssets || {
            small: [],
            medium: [],
            large: [],
          },
        },
      });
      initialized.current = true;
    }
  }, [promotion, searchParams?.id, open]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={loading}
      title="Update Promotion"
      description="Update the details of this promotion"
      renderActions={() => (
        <OfficeButton
          type="button"
          size="sm"
          loading={updating}
          disabled={updating}
          onClick={wrapClick(form.handleSubmit)}
        >
          {updating ? "Please wait..." : "Update"}
        </OfficeButton>
      )}
    >
      <PromotionForm form={form} />
    </Modal>
  );
}
